// Single source of truth for the game directory. Used by the apex landing page
// to render the directory, by the stats page to map a slug to a display name,
// and by the "games in progress" rejoin links to build a room URL.

import { config } from "./config.js";

export interface GameEntry {
  slug: string;
  name: string;
  url: string;
  description: string;
}

export const GAME_DIRECTORY: GameEntry[] = [
  {
    slug: "beachtowels",
    name: "Beach Towels",
    url: "https://beachtowels.couplesgames.click",
    description:
      "Take turns placing 2×1 towels on a shared grid — the first player without a legal placement loses.",
  },
  {
    slug: "blackhole",
    name: "Black Hole",
    url: "https://blackhole.couplesgames.click",
    description:
      "Claim circles on a pyramid of circles — the lone circle left becomes the black hole, and the lower sum touching it wins.",
  },
  {
    slug: "corners",
    name: "Corners",
    url: "https://corners.couplesgames.click",
    description:
      "A two-player game of squares — claim tiles, complete squares, and capture everything inside, chain reactions included.",
  },
  {
    slug: "hiddencity",
    name: "Hidden City",
    url: "https://hiddencity.couplesgames.click",
    description:
      "Build a secret city, then investigate your opponent's hidden grid turn by turn to reconstruct it first.",
  },
  {
    slug: "journey",
    name: "Journey",
    url: "https://journey.couplesgames.click",
    description:
      "A western take on the classic stick-race — throw the sticks, ride your four horses around the trail, and be first to bring them home.",
  },
  {
    slug: "orderchaos",
    name: "Order and Chaos",
    url: "https://orderchaos.couplesgames.click",
    description:
      "One of you is Order, the other Chaos. Place X's and O's — Order wins on any line, Chaos wins by filling the board without one.",
  },
  {
    slug: "prophecy",
    name: "Prophecy",
    url: "https://prophecy.couplesgames.click",
    description:
      "Take turns writing predictions into a shared grid — a number scores only when it matches the count in its row or column.",
  },
  {
    slug: "rowcall",
    name: "Row Call",
    url: "https://rowcall.couplesgames.click",
    description:
      "Call a row or column, your opponent picks the cell — your color fills it. First to line up your color wins.",
  },
  {
    slug: "splatter",
    name: "Splatter",
    url: "https://splatter.couplesgames.click",
    description:
      "Split the board into two colors, pick your side, then splatter dots — small pops or big explosions — and own the most squares.",
  },
  {
    slug: "wordchain",
    name: "Word Chain",
    url: "https://wordchain.couplesgames.click",
    description:
      "Build a secret word chain and take turns guessing to complete your opponent's chain first.",
  },
];

export function gameNameForSlug(slug: string): string {
  return GAME_DIRECTORY.find((g) => g.slug === slug)?.name ?? slug;
}

export function gameUrlForSlug(slug: string): string | null {
  return GAME_DIRECTORY.find((g) => g.slug === slug)?.url ?? null;
}

/**
 * The game slug for the current app, derived from its origin hostname
 * (e.g. "corners" for https://corners.couplesgames.click). Returns null on the
 * apex (no subdomain).
 */
export function currentGameSlug(): string | null {
  try {
    const host = new URL(config.appOrigin).hostname;
    const parts = host.split(".");
    if (parts.length < 3) return null;
    return parts[0] ?? null;
  } catch {
    return null;
  }
}
